import { Tooltip, Typography } from '@mui/material';
import { Row } from '../../shared/Panel';
import { dateTimeNoSeconds } from '../../shared/dateTime';
import { useFeature } from '../featureFlags/FeatureGate';
import {
  FEATURE_TRADE_TRANSFER_REASONS,
  lastTransferOf,
} from '../tradeReasons/tradeReasonsApi';

/**
 * Powód ostatniego przekazania do Trade albo zwrotu do FTL na karcie i pod
 * stanem na liście (makieta back office v2, A6; przełącznik
 * `tradeTransferReasons`, 20.10.2026). Kolumny `tradeTransfer*` z API — tekst
 * z chwili przejścia (etykieta powodu i komentarz). Pełna historia jest
 * w „Historii zmian”.
 */

type Loose = Record<string, unknown>;

/** „Przekazane do Trade 20.10.2026, 14:05: brak … — …”; `null` = brak przejścia. */
export function tradeTransferText(record: Loose | null | undefined): {
  label: string;
  text: string;
  at: string | null;
} | null {
  const t = lastTransferOf(record);
  if (!t) return null;
  return {
    label: t.kind === 'handover' ? 'Powód przekazania do Trade' : 'Powód zwrotu z Trade',
    text: t.text ?? 'bez powodu z listy',
    at: t.at,
  };
}

/** Wiersz „Powód przekazania do Trade” w boksie „Stan” karty. */
export const TradeTransferRow = ({ record }: { record: Loose }) => {
  const on = useFeature(FEATURE_TRADE_TRANSFER_REASONS);
  const info = on ? tradeTransferText(record) : null;
  if (!info) return null;
  return (
    <Row label={info.label}>
      <span data-testid="trade-transfer-reason">{info.text}</span>
      {info.at ? (
        <Typography variant="caption" color="text.secondary" component="span">
          {' '}
          · {dateTimeNoSeconds(info.at)}
        </Typography>
      ) : null}
    </Row>
  );
};

/** Jedna linia pod stanem (lista, pasek podsumowania okna zlecenia). */
export const TradeTransferCaption = ({ record }: { record: Loose }) => {
  const on = useFeature(FEATURE_TRADE_TRANSFER_REASONS);
  const info = on ? tradeTransferText(record) : null;
  if (!info) return null;
  const full = `${info.label}: ${info.text}${info.at ? ` (${dateTimeNoSeconds(info.at)})` : ''}`;
  return (
    <Tooltip title={full}>
      <Typography
        variant="caption"
        color="text.secondary"
        noWrap
        component="p"
        // Wąsko, z pełnym tekstem w dymku — kolumna „Stan planowania” nie może
        // się poszerzyć: lista ma się mieścić przy 1366 px (decyzja 08.10.2026).
        sx={{ maxWidth: 140 }}
        data-testid="trade-transfer-caption"
      >
        {info.text}
      </Typography>
    </Tooltip>
  );
};
